import classNames from "classnames";
import { Icons } from "prefab";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Position, Tooltip } from "@blueprintjs/core";
import { FormattedMessage } from "react-intl";
import { TablePagination } from "workbench";
import { CAMPAIGN_ACTIONS, CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST } from "../../../../../constants";
import { TAG_TYPE } from "../../../../../constants";
import { isDeleteEnabled } from "../../../../../utils/campaigns";
import ButtonStyle from "../../../../../styles/Button.module.scss";
import styles from "../../../../../styles/Campaigns/Campaigns.module.scss";
import tooltipStyles from "../../../../../styles/ToolTip.module.scss";
import DropdownStyles from "../../../../../styles/Dropdown.module.scss";
import formatValidityPeriodDisplay from "../../../../../utils/formatValidityPeriodDisplay";
import ActionIcon from "../../../../common/ActionIcon";
import Collapsible from "../../../../common/Collapsible";
import InfoBlockLoading from "../../../../common/InfoBlockLoading";
import LocalizedButton from "../../../../common/LocalizedButton";
import PageLoadingWithTable from "../../../../common/PageLoadingWithTable";
import Popup from "../../../../common/Popup";
import FilterChips from "../../../../common/Filters/FilterChips";
import Filters from "../../../../common/Filters/Filters";
import CampaignTree from "../../CampaignStructure/CampaignTree";
import Selector from "../../../../common/Selector";
import { getScrollableSummaryBlock } from "../../utils";
import CriteriaList from "../CriteriaList";

const {
  EditIcon,
  DeleteIcon,
  CopyIcon,
  ChevronDownIcon,
  LocationIcon,
  TheatreIcon,
  CueFromStartIcon,
  SearchIcon,
  CancelIcon,
  PauseIcon,
  PlayIcon,
  RemoveIcon,
} = Icons;

const renderTargetGroupNameTooltip = (name) => (
  <Tooltip
    className={classNames(tooltipStyles.toolTip, styles.listItemTooltip)}
    position={Position.AUTO}
    content={<div style={{ maxWidth: "250px", wordBreak: "break-word" }}>{name}</div>}
    usePortal={true}
    hoverOpenDelay={300}
  >
    <span className={styles.listItemName}>{name}</span>
  </Tooltip>
);

export default class TargetGroupDetails extends Component {
  static propTypes = {
    targetGroups: PropTypes.array.isRequired,
    withCampaignTree: PropTypes.bool,
    showEstimates: PropTypes.bool,
    showAdd: PropTypes.bool,
    showEdit: PropTypes.bool,
    showDuplicate: PropTypes.bool,
    showDelete: PropTypes.bool,
    showSplit: PropTypes.bool,
    showActionMenu: PropTypes.bool,
    onCancel: PropTypes.func,
    onClearCancel: PropTypes.func,
    onPause: PropTypes.func,
    onResume: PropTypes.func,
    onClearPause: PropTypes.func,
    serverBacked: PropTypes.bool,
    totalCount: PropTypes.number,
    page: PropTypes.number,
    ps: PropTypes.number,
    filters: PropTypes.array,
    searchQuery: PropTypes.string,
    onSearchChange: PropTypes.func,
    onFilterChange: PropTypes.func,
    onQueryChange: PropTypes.func,
  };

  static defaultProps = {
    showAdd: false,
    showEstimates: false,
    withCampaignTree: false,
    showEdit: true,
    showDuplicate: true,
    showDelete: true,
    showSplit: false,
    showActionMenu: false,
    serverBacked: false,
    totalCount: 0,
    page: 1,
    ps: 100,
    filters: [],
    searchQuery: "",
  };

  state = {
    isPopupOpen: false,
    isCampaignStructureLoading: true,
    campaignStructure: {},
    isCampaignStructureError: false,
    searchQuery: "",
  };

  // fetchCampaignTreeData = () => {
  //   const { params } = this.props;

  //   getCampaignTreeApi(params.campaignId)
  //     .then((campaignRes) =>
  //       this.setState({
  //         isCampaignStructureLoading: false,
  //         campaignStructure: campaignRes,
  //       })
  //     )
  //     .catch((err) => {
  //       this.setState({
  //         isCampaignStructureLoading: false,
  //         isCampaignStructureError: true,
  //         campaignStructure: null,
  //       });
  //     });
  // };

  handleEditClick = (event, id) => {
    event.stopPropagation();
    if (this.props.onEdit) {
      this.props.onEdit(id);
    }
  };

  handleDuplicateClick = (event, id) => {
    event.stopPropagation();
    if (this.props.onDuplicate) {
      this.props.onDuplicate(id);
    }
  };

  handleTargetGroupCreate = (event) => {
    event.stopPropagation();
    if (this.props.onTargetGroupCreate) {
      this.props.onTargetGroupCreate();
    }
  };

  handleConfirmDelete = (event) => {
    event.stopPropagation();
    if (this.props.onDelete) {
      this.props.onDelete(this.state.selectedId);
    }

    this.setState({
      isPopupOpen: false,
    });
  };

  handleDeleteClick = (event, id) => {
    event.stopPropagation();

    this.setState({
      isPopupOpen: true,
      selectedId: id,
    });
  };

  onDiscardChanges = (event) => {
    event.stopPropagation();

    this.setState({
      isPopupOpen: false,
    });
  };

  renderActionMenu = (targetGroup) => {
    const { showEdit, showSplit, showDuplicate, showDelete, campaign } = this.props;
    const isAllowed = (action) =>
      CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[action].includes(targetGroup.status);
    const dropdown = [
      {
        text: "Edit",
        icon: <EditIcon />,
        onClick: (event) => this.handleEditClick(event, targetGroup.id),
        hideMenuItem: !showEdit || !isAllowed(CAMPAIGN_ACTIONS.EDIT),
      },
      {
        text: "Split",
        icon: <CueFromStartIcon />,
        onClick: (event) => this.handleSplitClick(event, targetGroup.id),
        hideMenuItem: !showSplit || !isAllowed(CAMPAIGN_ACTIONS.SPLIT),
      },
      {
        text: "Duplicate",
        icon: <CopyIcon />,
        onClick: (event) => this.handleDuplicateClick(event, targetGroup.id),
        hideMenuItem: !showDuplicate,
      },
      {
        text: "Delete",
        icon: <DeleteIcon />,
        onClick: (event) => this.handleDeleteClick(event, targetGroup.id),
        disabled: !isDeleteEnabled(targetGroup, campaign?.status),
        hideMenuItem: !showDelete || !isDeleteEnabled(targetGroup, campaign?.status),
      },
      {
        text: "Pause",
        icon: <PauseIcon />,
        onClick: () => this.props.onPause(targetGroup),
        hideMenuItem: !isAllowed(CAMPAIGN_ACTIONS.PAUSE),
      },
      {
        text: "Clear Pause",
        icon: <RemoveIcon />,
        onClick: () => this.props.onClearPause(targetGroup),
        hideMenuItem: !isAllowed(CAMPAIGN_ACTIONS.PAUSE),
      },
      {
        text: "Resume",
        icon: <PlayIcon />,
        onClick: () => this.props.onResume(targetGroup),
        hideMenuItem: !isAllowed(CAMPAIGN_ACTIONS.RESUME),
      },
      {
        text: "Cancel",
        icon: <CancelIcon />,
        onClick: () => this.props.onCancel(targetGroup),
        hideMenuItem: !isAllowed(CAMPAIGN_ACTIONS.CANCEL),
      },
      {
        text: "Clear Cancel",
        icon: <RemoveIcon />,
        onClick: () => this.props.onClearCancel(targetGroup),
        hideMenuItem: !isAllowed(CAMPAIGN_ACTIONS.CANCEL),
      },
    ];
    return (
      <ActionIcon
        iconProps={[
          {
            toolTip: "Tooltip.moreActions",
            iconName: "MoreVerticalIcon",
            dropdown,
            dropdownClassName: DropdownStyles.unboundedDropdown,
          },
        ]}
      />
    );
  };

  renderTargetGroupsCriteria = (targetGroup) => {
    const { showEstimates, countries, isDetailsLoading } = this.props;
    if (isDetailsLoading) {
      return (
        <div className={styles.collapsibleBodyContainer}>
          <InfoBlockLoading />
        </div>
      );
    }

    if (!targetGroup?.criteria) {
      return <div className={styles.collapsibleBodyContainer}>No Criteria Found</div>;
    }

    return (
      <div className={styles.collapsibleBodyContainer}>
        {showEstimates &&
          getScrollableSummaryBlock(
            {
              validity: targetGroup?.summary?.validity,
              maxPlaysPerScreen: targetGroup?.summary?.maxPlaysPerScreen,
              ...targetGroup?.summary?.estimated,
            },
            {
              xs: 12,
              sm: 4,
              md: 3,
              lg: 3,
            }
          )}
        <div className={styles.criteriaContainer}>
          <CriteriaList
            targetGroupType="theatre"
            criteria={targetGroup?.criteria}
            headerIcon={<TheatreIcon />}
            countries={countries}
            headerName="Theatre"
            withEdit={false}
          />
          <CriteriaList
            targetGroupType="location"
            criteria={targetGroup?.criteria}
            headerIcon={<LocationIcon />}
            countries={countries}
            headerName="Location"
            withEdit={false}
          />
        </div>
      </div>
    );
  };

  handleSplitClick = (event, id) => {
    event.stopPropagation();
    if (this.props.onSplit) {
      this.props.onSplit(id);
    }
  };

  render() {
    const {
      targetGroups,
      showDelete,
      showDuplicate,
      showEdit,
      showAdd,
      showSplit,
      showActionMenu,
      withCampaignTree,
      isLoading,
      serverBacked,
      totalCount,
      page,
      ps,
      filters,
      searchQuery: remoteSearchQuery,
      onSearchChange,
      onFilterChange,
      onQueryChange,
    } = this.props;
    const { isPopupOpen, isCampaignStructureError } = this.state;
    const searchQuery = serverBacked ? remoteSearchQuery : this.state.searchQuery;

    if (isLoading && !serverBacked) return <PageLoadingWithTable />;

    const filteredTargetGroups = serverBacked
      ? targetGroups
      : targetGroups.filter((tg) => tg.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const displayedTotalCount = serverBacked ? totalCount : filteredTargetGroups.length;
    const currentPage = serverBacked ? page : 1;
    const pageStart = (currentPage - 1) * ps;
    const pageEnd = Math.min(pageStart + filteredTargetGroups.length, displayedTotalCount);

    return (
      <>
        <div className="col-12 clearfix flex">
          <div className={classNames("col-12", styles.listContent)}>
            <div className={styles.listHeader}>
              <div className={styles.listHeaderLeft}>
                <div className={styles.listHeaderTitle}>
                  <FormattedMessage id="TargetGroup" defaultMessage="Target Group" />
                  <span className={styles.countBadge}>{targetGroups.length}</span>
                </div>
                <div className={styles.searchWrapper}>
                  <input
                    className={styles.searchInput}
                    placeholder="Search Target Group"
                    value={searchQuery}
                    onChange={(e) =>
                      serverBacked
                        ? onSearchChange(e.target.value)
                        : this.setState({ searchQuery: e.target.value })
                    }
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className={styles.searchClearButton}
                      aria-label="Clear target group search"
                      onClick={() =>
                        serverBacked ? onSearchChange("") : this.setState({ searchQuery: "" })
                      }
                    >
                      <CancelIcon />
                    </button>
                  )}
                  <span
                    className={classNames(styles.searchIconWrapper, {
                      [styles.searchIconWithClear]: Boolean(searchQuery),
                    })}
                  >
                    <SearchIcon />
                  </span>
                </div>
                {serverBacked && (
                  <div className={styles.filterControl}>
                    <Filters
                      tagTypes={[TAG_TYPE.THEATRE, TAG_TYPE.SCREEN, TAG_TYPE.VALIDITY]}
                      selected={filters}
                      onFilterChange={onFilterChange}
                    />
                    {filters.length > 0 && (
                      <span
                        className={styles.filterCount}
                        aria-label={`Filter (${filters.length})`}
                      >
                        {filters.length}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {showAdd && (
                <LocalizedButton
                  text="Button.createTargetGroup"
                  iconName="AddFilledIcon"
                  onClick={this.handleTargetGroupCreate}
                  className={`${ButtonStyle.addButton} ${styles.listHeaderAddButton}`}
                />
              )}
            </div>
            {serverBacked && (
              <FilterChips
                selected={filters}
                clearLabel="Clear All"
                onFilterChange={onFilterChange}
              />
            )}
            {filteredTargetGroups.length === 0 && (
              <div className={styles.noSearchResults}>
                No results found. Try adjusting your search or filters.
              </div>
            )}
            {filteredTargetGroups.map((targetGroup, index) => {
              return (
                <Collapsible
                  key={index}
                  className="col-12 flex flex-column"
                  header={(isOpen, toggleCollapse) => (
                    <div
                      className={styles.collapsibleHeaderContainer}
                      onClick={() => {
                        this.props.getTargetGroupDetails(targetGroup.id, "list");
                        toggleCollapse();
                      }}
                    >
                      <div className={classNames(styles.title, styles.targetGroupTitle)}>
                        <span
                          className={classNames(styles.icon, {
                            [styles.open]: isOpen,
                            [styles.close]: !isOpen,
                          })}
                        >
                          <ChevronDownIcon />
                        </span>
                        {renderTargetGroupNameTooltip(targetGroup.name)}
                      </div>
                      <div className={classNames(styles.subTitle, styles.targetGroupSubTitle)}>
                        <div className={styles.info}>
                          {targetGroup.noOfScreens ? `${targetGroup.noOfScreens} Screens` : null}
                        </div>
                        <div className={styles.info}>
                          {formatValidityPeriodDisplay(targetGroup?.validity, "DD MMM YYYY")}
                        </div>
                      </div>
                      <div className={styles.collapseActions}>
                        {showActionMenu && this.renderActionMenu(targetGroup)}
                        {!showActionMenu &&
                          showEdit &&
                          CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[CAMPAIGN_ACTIONS.EDIT].includes(
                            targetGroup.status
                          ) && (
                            <div
                              className={styles.button}
                              onClick={(event) => this.handleEditClick(event, targetGroup.id)}
                            >
                              <EditIcon />
                            </div>
                          )}
                        {!showActionMenu &&
                          showSplit &&
                          CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[CAMPAIGN_ACTIONS.SPLIT].includes(
                            targetGroup.status
                          ) && (
                            <div
                              className={styles.button}
                              onClick={(event) => this.handleSplitClick(event, targetGroup.id)}
                            >
                              <CueFromStartIcon />
                            </div>
                          )}
                        {!showActionMenu && showDuplicate && (
                          <div
                            className={styles.button}
                            onClick={(event) => this.handleDuplicateClick(event, targetGroup.id)}
                          >
                            <CopyIcon />
                          </div>
                        )}
                        {!showActionMenu && showDelete && (
                          <div
                            className={
                              isDeleteEnabled(targetGroup, this.props.campaign?.status)
                                ? styles.button
                                : styles.disabledBtn
                            }
                            onClick={(event) => {
                              if (isDeleteEnabled(targetGroup, this.props.campaign?.status)) {
                                this.handleDeleteClick(event, targetGroup.id);
                              } else {
                                event.stopPropagation();
                              }
                            }}
                          >
                            <DeleteIcon />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  body={() => this.renderTargetGroupsCriteria(targetGroup)}
                />
              );
            })}
            {serverBacked && (
              <div className={styles.paginationFooter}>
                <span className={styles.paginationSummary}>
                  {displayedTotalCount === 0
                    ? "Showing 0 of 0"
                    : `Showing ${pageStart + 1}-${pageEnd} of ${displayedTotalCount}`}
                </span>
                <div className={styles.paginationControls}>
                  <div className={styles.pageSizeSelector}>
                    <Selector
                      showLabelInButton={false}
                      singleSelect
                      selectedList={[ps]}
                      onSelect={(value) => onQueryChange("ps", value)}
                      list={[25, 50, 100, 200]}
                      placeHolder="Show"
                      usePortal={false}
                    />
                  </div>
                  <TablePagination
                    totalRecords={displayedTotalCount}
                    pageSize={ps}
                    pageNeighbours={1}
                    onPageChange={(value) => onQueryChange("page", value)}
                    currentPage={currentPage}
                  />
                </div>
              </div>
            )}
          </div>
          {withCampaignTree && !isCampaignStructureError && (
            <div className={styles.campaignTree}>
              <CampaignTree
              // isLoading={isCampaignStructureLoading}
              // campaignTreeData={campaignStructure ?? []}
              />
            </div>
          )}
        </div>
        <Popup
          isOpen={isPopupOpen}
          onClose={this.onDiscardChanges}
          onConfirm={this.handleConfirmDelete}
          title="Popup.title"
          body="Popup.body"
        />
      </>
    );
  }
}
